import {
  NumberInput, Button, ActionIcon, Group, Table, Text,
} from '@mantine/core';
import { IconTrash, IconPlus } from '@tabler/icons-react';
import PropTypes from 'prop-types';
import { useState } from 'react';
import prepareTranslate from '@/helpers/dictionary';
import styles from './style.module.css';

const TieredCommissionInput = (props) => {
  const {
    value = [],
    onChange,
    label,
    error,
  } = props;

  const t = prepareTranslate();
  const [touchedRates, setTouchedRates] = useState(new Set());

  // Updates the rate of a specific tier
  const handleRateChange = (index, newRate) => {
    const updated = value.map((tier, i) => (i === index ? { ...tier, rate: newRate ?? 0 } : tier));
    onChange(updated);
  };

  // Updates the 'from' value of the first tier only
  const handleFromChange = (newFrom) => {
    const updated = value.map((tier, i) => (i === 0 ? { ...tier, from: newFrom ?? 0 } : tier));
    onChange(updated);
  };

  // Updates the 'to' value of the last tier
  const handleToChange = (index, newTo) => {
    const updated = value.map((tier, i) => (
      i === index ? { ...tier, to: newTo === '' || newTo === undefined ? null : newTo } : tier
    ));
    onChange(updated);
  };

  // Adds a new tier, starting where the previous tier ended
  const handleAdd = () => {
    const lastTier = value[value.length - 1];
    onChange([...value, { from: lastTier.to, to: null, rate: 0 }]);
  };

  // Removes a tier and resets the previous tier's 'to' to null
  const handleDelete = (index) => {
    const updated = value.filter((_, i) => i !== index);
    if (updated.length > 0) {
      updated[updated.length - 1] = { ...updated[updated.length - 1], to: null };
    }
    onChange(updated);
    setTouchedRates((prev) => new Set([...prev].filter((i) => i !== index)));
  };

  // Returns an error if the rate is not higher than the previous tier's rate
  const getRateError = (index) => {
    if (index === 0) {
      return null;
    }
    const isInvalid = (value[index]?.rate ?? 0) <= (value[index - 1]?.rate ?? 0);

    if (!isInvalid) {
      return null;
    }

    if (touchedRates.has(index) || error) {
      return t('components.tieredCommissionInput.rateError');
    }
    return null;
  };

  const lastTier = value[value.length - 1];
  const canAdd = lastTier?.to !== null && lastTier?.to !== undefined && lastTier?.to > 0;

  const headers = [
    t('components.tieredCommissionInput.headers.from'),
    t('components.tieredCommissionInput.headers.to'),
    t('components.tieredCommissionInput.headers.rate'),
  ];

  const tiers = value.map((tier, index) => (
    <Table.Tr key={index}>
      <Table.Td>
        {index === 0 ? (
          <NumberInput
            value={tier.from}
            onChange={(v) => handleFromChange(v)}
            min={0}
            thousandSeparator=','
            hideControls
          />
        ) : (
          <NumberInput value={tier.from} disabled thousandSeparator=',' hideControls />
        )}
      </Table.Td>
      <Table.Td>
        {index === value.length - 1 ? (
          <NumberInput
            value={tier.to ?? ''}
            onChange={(v) => handleToChange(index, v)}
            placeholder={t('components.tieredCommissionInput.noLimit')}
            min={tier.from + 1}
            thousandSeparator=','
            hideControls
          />
        ) : (
          <NumberInput value={tier.to} disabled thousandSeparator=',' hideControls />
        )}
      </Table.Td>
      <Table.Td>
        <NumberInput
          value={tier.rate}
          onChange={(v) => handleRateChange(index, v)}
          onBlur={() => setTouchedRates((prev) => new Set([...prev, index]))}
          error={getRateError(index)}
          min={0}
          max={100}
          hideControls
        />
      </Table.Td>
      <Table.Td>
        {value.length > 1 && index === value.length - 1 && (
          <ActionIcon color='red' variant='subtle' onClick={() => handleDelete(index)}>
            <IconTrash size={14} />
          </ActionIcon>
        )}
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <div>
      <Text size={'xs'} fw={'bold'}>{label}</Text>
      <Table withTableBorder={false} withColumnBorders={false}>
        <Table.Thead>
          <Table.Tr className={styles.headerRow}>
            {headers.map((header, ind) => <Table.Th key={ind}><Text size={'xs'} fw={'bold'}>{header}</Text></Table.Th>)}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody> {tiers} </Table.Tbody>
      </Table>
      <Group mt='xs'>
        <Button
          size='xs'
          variant='outline'
          leftSection={<IconPlus size={14} />}
          onClick={handleAdd}
          disabled={!canAdd}
        >
          {t('components.tieredCommissionInput.addTier')}
        </Button>
      </Group>
    </div>
  );
};

TieredCommissionInput.propTypes = {
  value: PropTypes.arrayOf(PropTypes.shape({
    from: PropTypes.number,
    to: PropTypes.number,
    rate: PropTypes.number,
  })),
  onChange: PropTypes.func,
  label: PropTypes.string,
  error: PropTypes.string,
};

export default TieredCommissionInput;
